import React from "react";
import ReactDOM from "react-dom/client";
import { ensureBrowserProcessEnv } from "./shims/processEnv";
import App from "./App";
import "./index.css";
import { initSentry } from "./config/sentry";
import { initProductAnalytics } from "./config/productAnalytics";
import { initWebVitals } from "./utils/performanceMonitoring";
import { authAPI } from "./services/api";
import { getAuthConfig } from "./services/configService";
import { hasAuthenticatedSession } from "./utils/authenticatedSession";
import { stashAndStripAgencyInvitationTokenFromAddressBar } from "./utils/agencyInvitationTokenSession";
import { useAuthStore } from "./stores/authStore";

// Ensure `process.env` exists in the browser before any config reads.
ensureBrowserProcessEnv();
stashAndStripAgencyInvitationTokenFromAddressBar();

// After a new deployment, old chunk filenames (e.g. ProviderListPage-BCggzAFB.js) no longer exist.
// If the user has a cached index.html or stale JS, dynamic imports return 404 and the app crashes.
// Reload once so they get the latest index.html and correct chunk URLs.
const CHUNK_LOAD_RELOAD_KEY = "chunk_load_reload_attempted";
function handleChunkLoadError(event: ErrorEvent): void {
  const msg = event.message ?? "";
  if (
    typeof msg === "string" &&
    (msg.includes("Failed to fetch dynamically imported module") ||
      msg.includes("Loading chunk") ||
      msg.includes("Loading CSS chunk"))
  ) {
    if (!sessionStorage.getItem(CHUNK_LOAD_RELOAD_KEY)) {
      sessionStorage.setItem(CHUNK_LOAD_RELOAD_KEY, "1");
      window.location.reload();
    }
  }
}
function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  const msg = event.reason?.message ?? String(event.reason ?? "");
  if (
    typeof msg === "string" &&
    (msg.includes("Failed to fetch dynamically imported module") ||
      msg.includes("Loading chunk") ||
      msg.includes("Importing a module script failed"))
  ) {
    if (!sessionStorage.getItem(CHUNK_LOAD_RELOAD_KEY)) {
      sessionStorage.setItem(CHUNK_LOAD_RELOAD_KEY, "1");
      event.preventDefault();
      window.location.reload();
    }
  }
}
if (typeof window !== "undefined") {
  window.addEventListener("error", handleChunkLoadError);
  window.addEventListener("unhandledrejection", handleUnhandledRejection);
}

// Global idle-timeout: if the user is inactive for the configured auth.sessionTimeout,
// automatically log out via backend and redirect to login. This complements token expiry.
// Session probe via hasAuthenticatedSession (auth flag + CSRF bootstrap evidence) —
// not the spoofable persist boolean alone, and not httpOnly cookies (invisible to JS).
function setupIdleTimeout() {
  if (typeof window === "undefined") return;

  const authConfig = getAuthConfig();
  const timeoutMs =
    typeof authConfig.sessionTimeout === "number" && authConfig.sessionTimeout > 0
      ? authConfig.sessionTimeout
      : 30 * 60 * 1000; // default 30 minutes

  let idleTimer: number | undefined;

  const resetTimer = () => {
    if (!hasAuthenticatedSession()) {
      if (idleTimer !== undefined) {
        window.clearTimeout(idleTimer);
        idleTimer = undefined;
      }
      return;
    }

    if (idleTimer !== undefined) {
      window.clearTimeout(idleTimer);
    }
    idleTimer = window.setTimeout(async () => {
      try {
        await authAPI.logout();
      } catch {
        // Ignore logout errors; cookies will eventually expire
      } finally {
        // Must clear persisted SPA auth before redirect, otherwise LoginPage
        // treats the user as still authenticated and bounces to the dashboard.
        try {
          useAuthStore.getState().clearAuth();
        } catch {
          // Client cleanup must not block redirect
        }
        if (window.location.pathname !== "/login") {
          window.location.href = "/login?reason=timeout";
        }
      }
    }, timeoutMs);
  };

  ["click", "mousemove", "keydown", "scroll", "focus"].forEach((event) =>
    window.addEventListener(event, resetTimer, { passive: true } as EventListenerOptions)
  );

  useAuthStore.subscribe((state, prev) => {
    if (state.isAuthenticated !== prev.isAuthenticated) {
      resetTimer();
    }
  });

  resetTimer();
}

setupIdleTimeout();

// Initialize Sentry error tracking (only in production)
initSentry();
// Initialize PostHog product analytics when VITE_POSTHOG_KEY is set
initProductAnalytics();

// Initialize Web Vitals performance monitoring
initWebVitals();

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
